/* jslint es6 */
import $ from 'zepto';
import { initPopups } from './tooltips';
import { objectHasOwnProperty } from '../../utils';

import {
  OUTER_MARKUP,
  INNER_MARKUP,
  NAVIGATION_PRODUCT,
} from '../../utils/legacy/templateConstants';

const countryPickers = {};
const triggerPickers = {};
const KEY_TAB = 9;
const KEY_LEFT = 37;
const KEY_RIGHT = 39;
const KEY_UP = 38;
const KEY_DOWN = 40;
const KEY_RETURN = 13;
const KEY_SPACE = 32;
const KEY_ESCAPE = 27;
const KEYDOWN = 'keydown';
const popups = initPopups();

function withPopupLinks($popupLinks, linkElement, callback) {
  let i;
  for (i = 0; i < $popupLinks.length; i += 1) {
    if ($popupLinks[i] === linkElement) {
      callback(i);
      break;
    }
  }
}

function handleDownRightKeys($popupLinks, self) {
  withPopupLinks($popupLinks, self, (i) => {
    if (i < $popupLinks.length - 1) {
      $popupLinks[i + 1].focus();
    }
  });
}
function handleUpLeftKeys($popupLinks, self) {
  withPopupLinks($popupLinks, self, (i) => {
    if (i > 0) {
      $popupLinks[i - 1].focus();
    }
  });
}

function handleTabKey(e, withAllLinks) {
  if (e.shiftKey) { // Shift-Tab out of menu
    withAllLinks((i, $allAnchors) => {
      if (i > 0) {
        $allAnchors[i - 1].focus();
      }
    });
  } else { // regular Tab out of menu
    withAllLinks((i, $allAnchors, allAnchorsLength) => {
      if (i < allAnchorsLength - 1) {
        $allAnchors[i + 1].focus();
      }
    });
  }
}

function createHtmlForCountries(currCountry, countries, numColumns, linkTemplate, navigationProduct) {
  const countriesPerColumn = Math.ceil(countries.length / numColumns);
  const CONTAINER_MARKUP = '<div class="navigation-popover" >{0}</div>';
  let html = '';
  let collected = '';
  let index = 0;
  let i = 0;
  let countryMatches;

  if (navigationProduct && navigationProduct.url && navigationProduct.name) {
    collected += NAVIGATION_PRODUCT.replaceTokens(navigationProduct.url, navigationProduct.name);
  }

  for (index = 0; index < numColumns; index += 1) {
    while ((i < (index + 1) * countriesPerColumn) && (i < countries.length)) {
      countryMatches = countries[i].code === currCountry;
      collected += INNER_MARKUP.replaceTokens(countryMatches ? 'class="selected navigation-popover__text"' : '',
        linkTemplate.replaceTokens(countries[i].url),
        countries[i].name);
      i += 1;
    }
    html += OUTER_MARKUP.replaceTokens(collected);
    collected = '';
  }
  return CONTAINER_MARKUP.replaceTokens(html);
}

export function initCountryPicker() {
  let pickersIndex = 0;

  function CountryPicker(popupId, pickerSelector, currCountry, countries, numColumns, linkTemplate, options) {
    const $countryElt = $(pickerSelector);
    let popup;
    let $popupLinks;

    function hidePopup() {
      if (popup) {
        popup.hide();
      }
    }

    function withAllLinks(callback) {
      // defensive programming: do not cache this because the dom could change since the last time this was called
      const $allAnchors = $('a');
      const allAnchorsLength = $allAnchors.length;
      let i;
      $allAnchors.forEach(($anchor) => {
        if ($anchor === $countryElt[0]) {
          callback(i, $allAnchors, allAnchorsLength);
        }
      });

      hidePopup();
    }

    function countryMenuLinkFocus() {
      $countryElt[0].focus();
      hidePopup();
      return false;
    }

    function goToHrefUrl({ href }) {
      window.location.pathname = href;
    }

    function linksKeyBinding(e) {
      const self = e.target;
      switch (e.which) {
        case KEY_DOWN: // focus on next link
        case KEY_RIGHT:
          handleDownRightKeys($popupLinks, self);
          return false;

        case KEY_UP:
        case KEY_LEFT: // focus on previous link
          handleUpLeftKeys($popupLinks, self);
          return false;

        case KEY_ESCAPE: // close menu, focus on country menu link
          countryMenuLinkFocus();
          break;

        case KEY_TAB:
          handleTabKey(e, withAllLinks);
          return false;

        case KEY_RETURN: // execute link action
          return goToHrefUrl(self);

        default:
          return false;
      }
      return false;
    }

    function createPopup(createPopupCallback) {
      popup = popups.get(popupId);
      if (!popup) {
        const html = createHtmlForCountries(currCountry, countries, numColumns,
          linkTemplate, options.navigationProduct);
        const popupOptions = $.extend({
          message: html,
          hideOnBodyClick: true,
        }, options || {});

        popup = popups.add(popupId, pickerSelector, popupOptions);

        $popupLinks = $('a:not(.xpop-close)', popup.get())
          .bind(KEYDOWN, linksKeyBinding);
      }
      if (createPopupCallback) {
        createPopupCallback();
      }
    }

    function requirePopup(requirePopupCallback) {
      if (!popup) {
        createPopup(requirePopupCallback);
      } else if (requirePopupCallback) {
        requirePopupCallback();
      }
    }

    function showPopup(showPopupCallback) {
      Object.keys(triggerPickers).forEach((key) => {
        if (objectHasOwnProperty(triggerPickers, key)) {
          $(triggerPickers[key]).attr('aria-expanded', false);
        }
      });
      requirePopup(() => {
        if (!popups.get(popupId).isActive()) {
          popups.hideAll();

          /* show the popup
           * pass in the target 'pickerSelector' because in some instances
           * the original target selector no longer exists on the page */
          popup.show(pickerSelector);
          $(pickerSelector).attr('aria-expanded', true);

          if (showPopupCallback) {
            showPopupCallback();
          }
        }
      });
      return false;
    }

    function removePopup() {
      if (popup) {
        popups.destroy(popupId);
      }
      $('body').undelegate(pickerSelector);
    }

    function focusCallback() {
      // focus on first link
      if ($popupLinks.length > 0) {
        $popupLinks[0].focus();
      }
    }

    function countryMenuLinkClickCallback() {
      if (popups.get(popupId) && popups.get(popupId).isActive()) {
        hidePopup();
      } else {
        showPopup();
      }
      return false;
    }

    function countryMenuLinkKeyDownCallback(e) {
      if (e.which === KEY_RETURN || e.which === KEY_DOWN || e.which === KEY_UP || e.which === KEY_SPACE) {
        showPopup(focusCallback);
        return false;
      }
      return true;
    }

    $('body')
      .delegate(pickerSelector, 'click', countryMenuLinkClickCallback)
      .delegate(pickerSelector, KEYDOWN, countryMenuLinkKeyDownCallback);

    return {
      hide: hidePopup,
      remove: removePopup,
    };
  }

  function add(pickerSelector, currCountry, countries, numColumns, linkTemplate, options) {
    const $elt = $(pickerSelector);
    let pickerId;
    let pickerSelectorId;
    let i;
    for (i = 0; i < $elt.length; i += 1) {
      pickersIndex += 1;
      pickerSelectorId = $elt[i].id || `picker-selector-${(pickersIndex)}`;

      if (!$elt[i].id) {
        $elt[i].id = pickerSelectorId;
      }

      pickerId = `${pickerSelectorId}-picker`;

      if (!countryPickers[pickerId]) {
        countryPickers[pickerId] = new CountryPicker(pickerId, `#${pickerSelectorId}`, currCountry,
          countries, numColumns, linkTemplate, options);
      }
      if (!triggerPickers[pickerId]) {
        triggerPickers[pickerId] = pickerSelector;
      }
    }
  }

  function removeAllPickers(callback) {
    Object.keys(countryPickers).forEach((key) => {
      if (objectHasOwnProperty(countryPickers, key)) {
        if (countryPickers[key] !== null) {
          countryPickers[key].remove();
          countryPickers[key] = null;
          delete countryPickers[key];
        }
      }
    });

    if (callback) {
      callback();
    }
  }

  return {
    initializeWidget: add,
    removeAllPickers,
  };
}

export default {
  initCountryPicker,
};
